import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Checkbox } from "@/components/ui/Checkbox/Checkbox";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Banner, Field, FormShell } from "@/components/auth/FormShell";
import { SamlSignInButton } from "@/components/auth/SamlSignInButton";
import { SsoRow } from "@/components/auth/SsoRow";
import formStyles from "@/components/auth/AuthForm.module.css";
import { useAuth } from "@/lib/AuthContext";
import { signInWithPassword, type AuthError, type Tokens } from "@/lib/auth";
import { discoverByEmailDomain, useSamlLookup } from "@/lib/queries";

/**
 * Email + password sign-in against the Cognito User Pool (SRP flow).
 * Disabled-user errors are mapped to a waitlist-aware message so users
 * who signed up but haven't been approved get a clear explanation
 * instead of a generic "incorrect credentials" string.
 */
export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth();
  // `:wsId` is set on `/signin/:wsId` — the workspace-scoped sign-in
  // page used by enterprise SAML SSO. When set we look the workspace
  // up and render a "Continue with SSO" button if SAML is configured.
  // The default `/signin` route leaves it undefined.
  const { wsId } = useParams<{ wsId?: string }>();
  const samlLookup = useSamlLookup(wsId);
  const next = params.get("next") ?? "/app";
  const justReset = params.get("reset") === "1";
  // Surface state forwarded from /auth/callback when a Google sign-in
  // bounced (waitlist gate, IdP refusal). `reason=waitlist` wins over
  // `error=…` so a disabled-account redirect always reads as waitlist;
  // the reset success banner is also suppressed when either is present
  // so the user sees the most-relevant message, not stacked banners.
  const reason = params.get("reason");
  // Errors come from the URL — clamp length so a malicious link can't
  // stuff phishing copy into a banner that looks official.
  const errorParamRaw = params.get("error");
  const errorParam = errorParamRaw ? errorParamRaw.slice(0, 200) : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetBannerVisible, setResetBannerVisible] = useState(justReset);

  // When Cognito returns the TOTP challenge, `totpSubmit` is set and we
  // render an "Enter your authenticator code" screen instead of the
  // email/password form. The closure captures the same CognitoUser
  // instance from the first authenticateUser call, so submitting the
  // code completes the same sign-in session.
  const [totpSubmit, setTotpSubmit] = useState<
    ((code: string) => Promise<Tokens>) | null
  >(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState<AuthError | null>(null);
  const [totpSubmitting, setTotpSubmitting] = useState(false);

  useEffect(() => {
    if (auth.status === "authenticated") navigate(next, { replace: true });
  }, [auth.status, navigate, next]);

  // Email-domain auto-detect: as the user types their email on the
  // default `/signin` route (NOT the per-workspace `/signin/:wsId`
  // route), debounce + look up the domain. If it maps to a workspace
  // with active SAML, route to that workspace's slug page where the
  // SAML button auto-renders. Done quietly — no error toast on miss,
  // no second redirect once we've already routed. Skipped on the slug
  // route to avoid an infinite hop.
  useEffect(() => {
    if (wsId) return; // already on the slug page
    const at = email.indexOf("@");
    if (at < 1 || at >= email.length - 1) return;
    // Avoid spamming the endpoint on every keystroke — 350ms debounce.
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          // SPA-side split: send only the domain. The email never
          // leaves the browser, so it doesn't land in access logs.
          const r = await discoverByEmailDomain(email.trim());
          if (r.hasSaml && r.workspaceId) {
            const nextParam = next === "/app" ? "" : `?next=${encodeURIComponent(next)}`;
            navigate(`/signin/${r.workspaceId}${nextParam}`, { replace: false });
          }
        } catch {
          // Silent — discovery is a nice-to-have, not a gate.
        }
      })();
    }, 350);
    return () => window.clearTimeout(handle);
  }, [email, wsId, next, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResetBannerVisible(false);
    setSubmitting(true);
    try {
      const result = await signInWithPassword(email.trim(), password, remember);
      if (result.kind === "totp") {
        // React useState stores a function literally when wrapped, since the
        // raw value would be interpreted as an updater function.
        setTotpSubmit(() => result.submit);
        return;
      }
      auth.refresh();
      navigate(next, { replace: true });
    } catch (err) {
      const authErr = err as AuthError;
      // Recovery path for someone who signed up but never typed the
      // verification code: bounce them to /signup/verify with their
      // email pre-filled, instead of leaving them stuck on a generic
      // error.
      if (authErr.kind === "user_not_confirmed") {
        navigate(`/signup/verify?email=${encodeURIComponent(email.trim())}`, {
          replace: true,
        });
        return;
      }
      setError(authErr);
    } finally {
      setSubmitting(false);
    }
  }

  async function onTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!totpSubmit) return;
    setTotpError(null);
    setTotpSubmitting(true);
    try {
      await totpSubmit(totpCode.trim());
      auth.refresh();
      navigate(next, { replace: true });
    } catch (err) {
      setTotpError(err as AuthError);
    } finally {
      setTotpSubmitting(false);
    }
  }

  function backToPassword() {
    setTotpSubmit(null);
    setTotpCode("");
    setTotpError(null);
  }

  if (totpSubmit) {
    return (
      <AuthLayout view="signin">
        <FormShell
          title="Enter your authenticator code"
          subtitle={`We need a 6-digit code to confirm it's you${email ? ` — ${email}` : ""}.`}
        >
          <form onSubmit={onTotpSubmit} noValidate className={formStyles.stack}>
            {totpError && (
              <Banner variant="danger" icon={<Icon name="info" size={14} />}>
                {totpError.message}
              </Banner>
            )}
            <Field label="6-digit code" htmlFor="totp-code">
              <Input
                id="totp-code"
                icon="lock"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
              />
            </Field>
            <Btn
              type="submit"
              variant="primary"
              full
              size="lg"
              loading={totpSubmitting}
              disabled={totpCode.length !== 6}
              iconRight={<Icon name="arrow-right" size={14} />}
            >
              Verify
            </Btn>
            <Btn type="button" variant="ghost" full onClick={backToPassword}>
              Use a different account
            </Btn>
          </form>
        </FormShell>
      </AuthLayout>
    );
  }

  // Only switch to SSO-flavored copy when we KNOW the workspace has
  // SAML configured. While the lookup is in-flight `data` is undefined;
  // when it returns `{hasSaml: false}` (workspace doesn't exist OR is
  // not configured), fall back to the default copy so a typo'd URL
  // doesn't read like a broken SSO page.
  const showSamlCopy = !!samlLookup.data?.hasSaml;
  return (
    <AuthLayout view="signin">
      <FormShell
        title={showSamlCopy ? "Sign in with SSO" : "Sign in to your workspace"}
        subtitle={
          showSamlCopy
            ? "Continue with your company's identity provider, or use email + password."
            : "Pick up wherever you left off in the queue."
        }
      >
        {samlLookup.data?.hasSaml && samlLookup.data.providerName && (
          <SamlSignInButton
            providerName={samlLookup.data.providerName}
            next={next}
          />
        )}
        <SsoRow next={next} />
        <form onSubmit={onSubmit} noValidate className={formStyles.stack}>
          {resetBannerVisible && !error && !reason && !errorParam && (
            <Banner variant="success" icon={<Icon name="check" size={14} />}>
              Password updated. Sign in with your new password.
            </Banner>
          )}
          {!error && reason === "waitlist" && (
            <Banner variant="waitlist" icon={<Icon name="info" size={14} />}>
              You’re on the waitlist — we’ll email you when access opens.
            </Banner>
          )}
          {!error && !reason && errorParam && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {errorParam}
            </Banner>
          )}
          {error && error.kind === "waitlist_pending" && (
            <Banner variant="waitlist" icon={<Icon name="info" size={14} />}>
              You’re on the waitlist — we’ll email you when access opens.
            </Banner>
          )}
          {error && error.kind !== "waitlist_pending" && (
            <Banner variant="danger" icon={<Icon name="info" size={14} />}>
              {error.message}
            </Banner>
          )}
          <Field label="Email" htmlFor="signin-email">
            <Input
              id="signin-email"
              icon="user"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brand.com"
              required
            />
          </Field>
          <Field
            label="Password"
            htmlFor="signin-password"
            right={
              <Link to="/reset" className={formStyles.forgotLink}>
                Forgot?
              </Link>
            }
          >
            <Input
              id="signin-password"
              icon="lock"
              type="password"
              autoComplete="current-password"
              suffix="show"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
            />
          </Field>
          <Checkbox
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            label="Keep me signed in on this device"
          />
          <Btn
            type="submit"
            variant="primary"
            full
            size="lg"
            loading={submitting}
            iconRight={<Icon name="arrow-right" size={14} />}
          >
            Sign in
          </Btn>
          <div className={formStyles.foot}>
            <Icon name="lock" size={12} color="var(--text-3)" />
            <span>Cognito-backed · session caps at 24h</span>
          </div>
        </form>
      </FormShell>
    </AuthLayout>
  );
}
